
import { 
  CharacterItem,
  CharacterSpell,
  CharacterAbility,
  Spell,
  Attack,
  Character,
  CharacterFeature,
  AbilityEffect,
  CharacterWeapon,
  CharacterInfusion,
  CharacterInfusionForAlly,
  StoredSpell
} from "@dndModels";
import { CastingTime, DamageType, SpellLevel, DurationType, CreatureFBType } from "@dndModels/Types";
import { DURATIONS, PASSIVE_CASTING_TIMES } from "@dndModels/Constants";

import CommonUtilities from "@utilities/common_utilities";

const common_util = CommonUtilities.getInstance();

export class CharacterAction {
  // true_id: string = "";
  type = "";
  special_resource = false;
  action: CharacterItem | CharacterSpell | CharacterAbility | Attack | StoredSpell | null = null;
  casting_time: CastingTime | "Light Attack" | "Legendary Action" | "Lair Action" = "A";
  special_casting_time = "";
  level: SpellLevel = 0; // If it uses spell slots, this is the lowest level
  ritual_only = false;
  no_ritual = false;
  lowest = false;
  range = "";
  special_action_type: "Attack" | "Ability" | "Spell" | "Item Action" | "None" = "None";
  pdf_height = 0;
  mod_flat = 0; // Change potence flats by this amount, but only if they're basic and not 0
  attack_mod = 0;
  dc = 0;
  resource_consumed = "";
  amount_consumed = 0;
  slot = false;
  and_special = false;
  disabled = false;
  damage_type_options: DamageType[] = [];
  extra_actions: CharacterAction[] = []; // These are like Extra Effects, but they're getting specially added when the Action is created.  The purpose is to make extra effects for when you're using different slots.
  feature_disabled: CharacterFeature | undefined = undefined;
  char_effects: CharacterEffect[] = [];
  infusion: CharacterInfusion | CharacterInfusionForAlly | undefined = undefined;
  creature_fb_type: CreatureFBType | "" = "";

  get favorite() {
    return this.action && !(this.action instanceof Attack) ? this.action.favorite : false;
  }

  set favorite(value: boolean) {
    if (this.action && !(this.action instanceof Attack)) {
      this.action.favorite = value;
    }
  }

  get true_id() {
    if (this.action) {
      if (this.action instanceof Attack) {
        return "Attack";
      } else {
        return this.action.true_id;
      }
    }
    return "null";
  }

  get name(): string {
    return this.getName();
  }

  get attack_stat() {
    if (this.action instanceof CharacterWeapon) {
      return this.action.attack_stat;
    }
    return "";
  }

  getName(character?: Character) {
    const spell = this.action;
    if (spell) {
      if (spell instanceof CharacterItem) { 
        return spell.get_use_name();
      } else if (spell instanceof Attack) {
        return character === undefined || character.unarmed_strike_customizations.name === "" ? "Unarmed Strike" : character.unarmed_strike_customizations.name;
      } else {
        let name = spell.true_name;
        if ((spell instanceof CharacterAbility || spell instanceof CharacterSpell) && 
          spell._name !== "") {
          name = spell._name;
        } else if (spell instanceof CharacterAbility && 
          // spell.the_ability instanceof SpellAsAbility && 
          spell.the_spell) {
          name = spell.the_spell.name;
        }
        return name;
      }
    }
    return "";
  }

  get description(): string {
    return this.getDescription();
  }

  getDescription(character?: Character) {
    const spell = this.action;
    if (spell) {
      if (spell instanceof Attack) {
        return character === undefined || character.unarmed_strike_customizations.name === "" ? "Unarmed Strike" : character.unarmed_strike_customizations.name;
      } else { 
        return spell.description;
      }
    }
    return "";
  }

  get spell(): Spell | null {
    if (this.action) {
      if ((this.action instanceof CharacterSpell || 
        this.action instanceof CharacterAbility) && 
        this.action.the_spell) {
        return this.action.the_spell;
      }
    }
    return null;
  }

  get hidden(): boolean {
    if (this.action instanceof CharacterItem || this.action instanceof Attack) {
      return false;
    } else if (this.action) {
      return this.action.hidden;
    } else {
      return false;
    }
  }

  get hide_in_pdf(): boolean {
    if (this.action instanceof CharacterItem || this.action instanceof Attack) {
      return false;
    } else if (this.action) {
      return this.action.hide_in_pdf;
    } else {
      return false;
    }
  }

  get is_passive(): boolean {
    if (this.action instanceof CharacterAbility) {
      return common_util.anyOverlaps(PASSIVE_CASTING_TIMES, this.action.casting_times);
    }
    return false;
  }

  get duration() {
    if (this.action instanceof CharacterSpell && DURATIONS.includes(this.action.duration)) {
      return this.action.duration as DurationType;
    } else if (this.action instanceof CharacterAbility && DURATIONS.includes(this.action.duration)) {
      return this.action.duration as DurationType;
    }
    return "Instantaneous";
  }

  copy(copyMe: CharacterAction): void {
    // this.true_id = copyMe.true_id;
    this.type = copyMe.type;
    this.special_resource = copyMe.special_resource;
    this.action = copyMe.action;
    this.casting_time = copyMe.casting_time;
    this.special_casting_time = copyMe.special_casting_time;
    this.level = copyMe.level;
    this.ritual_only = copyMe.ritual_only;
    this.no_ritual = copyMe.no_ritual;
    this.lowest = copyMe.lowest;
    this.range = copyMe.range;
    this.special_action_type = copyMe.special_action_type;
    this.extra_actions = copyMe.extra_actions.map(o => o.clone());
    this.creature_fb_type = copyMe.creature_fb_type;
  }

  clone(): CharacterAction {
    const new_obj = new CharacterAction();
    new_obj.copy(this);
    return new_obj;
  }
}

export interface CharacterEffect {
  effect?: AbilityEffect;
  attack: Attack;
  dc: number;
}